import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Submission={
  submissionId:string;
  academicYearId:string;
  yearCode:string;
  templateVersionId:string;
  templateName:string;
  templateVersionLabel:string;
  revision:number;
  status:string;
  submitNote?:string|null;
  submittedAt:string;
  reviewedAt?:string|null;
  decisionNote?:string|null;
  recipientName:string;
};

type Workspace={
  recipient?:{organizationId:string;name:string;type:string}|null;
  submissions:Submission[];
};

function label(value:string){
  const map:Record<string,string>={
    SUBMITTED:"제출됨",
    RETURNED:"보완 요청",
    APPROVED:"승인",
    WITHDRAWN:"철회"
  };
  return map[value] || value;
}

function format(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

export function CenterReportSubmissionPanel({
  client,schoolId,academicYearId,templateVersionId
}:{
  client:PlatformClient;
  schoolId:string;
  academicYearId:string;
  templateVersionId:string;
}){
  const [workspace,setWorkspace]=useState<Workspace|null>(null);
  const [note,setNote]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_report_submission_workspace",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setWorkspace(data as Workspace);
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const yearSubmissions=useMemo(
    ()=>(workspace?.submissions || []).filter((item)=>item.academicYearId===academicYearId),
    [workspace,academicYearId]
  );
  const latest=yearSubmissions[0] || null;
  const blocked=latest?.status==="SUBMITTED" || latest?.status==="APPROVED";
  const canSubmit=Boolean(workspace?.recipient && academicYearId && templateVersionId && !blocked);

  async function submit(){
    setBusy(true);setError("");setMessage("");
    const {data,error}=await client.rpc("center_submit_school_report",{
      p_school_id:schoolId,
      p_academic_year_id:academicYearId,
      p_template_version_id:templateVersionId,
      p_note:note.trim() || null
    });
    setBusy(false);
    if(error){setError(error.message);return;}
    const result=data as {revision?:number;recipientName?:string};
    setMessage(`${result.recipientName || workspace?.recipient?.name || "상위기관"}에 ${result.revision || ""}차 보고서를 제출했습니다.`);
    setNote("");
    setRefresh((value)=>value+1);
  }

  return (
    <section className="erp08-submission-card report-no-print">
      <div className="panel-title">
        <div>
          <strong>공식 보고서 제출</strong>
          <span>제출 시 현재 보고서가 스냅샷으로 고정되며 이후 검토 이력이 남습니다.</span>
        </div>
        <span className={latest?.status==="APPROVED" ? "status-pill good" : "status-pill"}>
          {latest ? label(latest.status) : "미제출"}
        </span>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="erp08-submission-summary">
        <div><span>수신기관</span><strong>{workspace?.recipient?.name || "상위기관 연결 필요"}</strong></div>
        <div><span>현재 차수</span><strong>{latest ? `${latest.revision}차` : "없음"}</strong></div>
        <div><span>최근 제출</span><strong>{latest ? format(latest.submittedAt) : "-"}</strong></div>
      </div>

      {latest?.decisionNote && (
        <div className={latest.status==="RETURNED" ? "erp08-decision returned" : "erp08-decision"}>
          <strong>{latest.status==="RETURNED" ? "보완 요청 내용" : "검토 메모"}</strong>
          <p>{latest.decisionNote}</p>
        </div>
      )}

      {!blocked && (
        <div className="erp08-submit-form">
          <label>제출 메모
            <textarea rows={3} value={note} onChange={(event)=>setNote(event.target.value)}
              placeholder="검토기관에 전달할 메모가 있으면 입력하세요." />
          </label>
          <button className="primary" disabled={busy || !canSubmit} onClick={submit}>
            {busy ? "제출 중…" : latest?.status==="RETURNED" ? "보완 후 재제출" : "공식 보고서 제출"}
          </button>
        </div>
      )}

      {latest?.status==="SUBMITTED" && <p className="muted">검토기관의 승인 또는 보완 요청 전에는 새 차수를 제출할 수 없습니다.</p>}
      {latest?.status==="APPROVED" && <p className="teacher-success">이 학년도의 공식 보고서가 승인되었습니다.</p>}

      {yearSubmissions.length>0 && (
        <div className="erp08-submission-history">
          <strong>제출 이력</strong>
          {yearSubmissions.map((item)=>(
            <div key={item.submissionId}>
              <span><b>{item.revision}차 · {label(item.status)}</b><small>{item.templateName} {item.templateVersionLabel}</small></span>
              <span>{format(item.submittedAt)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
